import { Address } from '@/common/types';
import {
  IClaimApprovalStatusAggregatorDtoClaimApprovalStatusCodeEnum as ClaimApprovalStatusCode,
  IOcrAccountOrderDtoAccountTypeEnum,
  IOcrDocumentAggregatorResponseDto,
  IOcrDocumentWarningDtoTypeEnum,
  IOcrFieldDto,
  IOcrServiceForOptionDtoRelationshipTypeEnum,
} from '@/dtos';

export { IAccountOrderDtoProofOfExpensesTypeEnum as IProofOfExpensesEnum } from '@/dtos';

export enum UseMyMoneyOption {
  REIMBURSE_MYSELF = 'REIMBURSE_MYSELF',
  PAY_A_PROVIDER = 'PAY_A_PROVIDER',
  HSA_WITHDRAWAL = 'HSA_WITHDRAWAL',
}

export const UseMyMoneyOptionTitle = {
  [UseMyMoneyOption.REIMBURSE_MYSELF]: 'Reimburse Myself',
  [UseMyMoneyOption.PAY_A_PROVIDER]: 'Pay a Provider',
  [UseMyMoneyOption.HSA_WITHDRAWAL]: 'HSA Withdrawal',
} as const;

export const UseMyMoneySummaryTitle = {
  [UseMyMoneyOption.REIMBURSE_MYSELF]: 'Claim Summary',
  [UseMyMoneyOption.PAY_A_PROVIDER]: 'Pay a Provider',
  [UseMyMoneyOption.HSA_WITHDRAWAL]: 'HSA Withdrawal',
} as const;

export enum UseMyMoneyStep {
  SELECT_OPTION = 'SELECT_OPTION',
  READY_TO_SCAN = 'READY_TO_SCAN',
  SUMMARY = 'SUMMARY',
}

export enum ReadyToScanStep {
  PREVIEW_FILE = 'PREVIEW_FILE',
  SCAN_FILE = 'SCAN_FILE',
}

export enum ReceiptUploadOption {
  YES_I_HAVE_ONE = 'YES_I_HAVE_ONE',
  NO_SKIP_UPLOAD = 'NO_SKIP_UPLOAD',
}

export enum UploadModalStep {
  Closed = 'closed',
  Upload = 'upload',
  ReadyToScan = 'readyToScan',
}

export interface CoveragePeriod {
  coverageDateStart?: string;
  coverageDateEnd?: string;
}

export enum ClaimSummaryField {
  AMOUNT = 'AMOUNT',
  PROVIDER = 'PROVIDER',
  SERVICE_FOR = 'SERVICE_FOR',
  DATE_OF_SERVICE = 'DATE_OF_SERVICE',
  SERVICE_DESCRIPTION = 'SERVICE_DESCRIPTION',
}

export const USE_MY_MONEY_UPLOAD_STORE_KEY = 'USE_MY_MONEY_UPLOAD_STORE_KEY';
export const USE_MY_MONEY_UPLOAD_NEW_FILE_STORE_KEY = 'USE_MY_MONEY_UPLOAD_NEW_FILE_STORE_KEY';

export const getUseMyMoneyOptionTitle = (option: UseMyMoneyOption): string => {
  return UseMyMoneyOptionTitle[option];
};

export const getUseMyMoneySummaryTitle = (option: UseMyMoneyOption): string => {
  return UseMyMoneySummaryTitle[option];
};

export interface OcrItem {
  value?: string;
  confidence?: number;
}

export interface OcrPatientItem {
  dependentId?: string;
  personId?: string;
  fullName?: string;
  receiptName?: string;
  confidence?: number;
}

export interface OcrPatient {
  dependentId?: string;
  personId?: string;
  fullName?: string;
  receiptName?: string;
}

export interface OcrWarning {
  warning?: string;
  fields?: string[];
  type?: IOcrDocumentWarningDtoTypeEnum;
}

export const OCR_ALL_MISSING_FIELDS = [
  'provider_id',
  'service_for',
  'service_on',
  'requested_amount',
  'service_id',
] as const;

export interface EligibleExpense {
  patient?: OcrPatient;
  service?: string;
  amount?: string;
  serviceDates?: string[];
}

export interface OcrDocument {
  account?: string;
  accounts?: OcrItem[];
  amount?: string;
  amounts?: OcrItem[];
  facilityName?: string;
  facilityNames?: OcrItem[];
  invoice?: string;
  invoices?: OcrItem[];
  patient?: string;
  patients?: OcrItem[];
  serviceDate?: string;
  serviceDates?: OcrItem[];
  serviceDescription?: string;
  serviceDescriptions?: OcrItem[];
  warnings?: OcrWarning[];
  eligibleExpenses?: EligibleExpense[];
}

export interface OpenaiOcrDocument {
  aiApproved?: boolean;
}

export interface OcrDocumentResponse extends Omit<IOcrDocumentAggregatorResponseDto, 'patient' | 'patients'> {
  patient?: string;
  patients?: Set<IOcrFieldDto>;
}

export enum GroupServicesKey {
  CATEGORY = 'CATEGORY',
  ACCOUNT = 'ACCOUNT',
}

export enum ClaimValidationError {
  OPTION_REQUIRED = 'OPTION_REQUIRED',
}

export const CLAIM_AUTOMATICALLY_APPROVAL_STATUS_CODES: ClaimApprovalStatusCode[] = [
  ClaimApprovalStatusCode.AUTOMATICALLY_APPROVED_AI_RECEIPT_APPROVED,
  ClaimApprovalStatusCode.AUTOMATICALLY_APPROVED_NO_RECEIPT_REQUIRED,
  ClaimApprovalStatusCode.AUTOMATICALLY_APPROVED_BY_SIGIS,
];

export const CLAIM_CREATED_WITHOUT_DOCUMENT_STATUS_CODES: ClaimApprovalStatusCode[] = [
  ClaimApprovalStatusCode.PROOF_OF_EXPENSE_NEEDED,
];

export interface Service {
  id: string;
  name: string;
  categoryType?: string;
}

export interface OcrProvider {
  providerId?: string;
  name?: string;
  taxId?: string;
  isNewProvider?: boolean;
};

export interface OcrServiceFor {
  id?: string;
  employeeId?: string;
  dependentId?: string;
  fullName?: string;
  relationshipType?: IOcrServiceForOptionDtoRelationshipTypeEnum;
  // address?: string;
  birthOn?: string;
  isSuggested?: boolean;
  receiptName?: string;
}

export interface OcrService {
  serviceId?: string;
  serviceName?: string;
  isSuggested?: boolean;
  categoryType?: string;
}

export interface OcrAmount {
  amount?: string;
  isSuggested?: boolean;
}

export interface OcrServiceDate {
  date?: string;
  isSuggested?: boolean;
}

export interface OcrProviderOption {
  id?: string;
  name?: string;
  // address?: string;
  isSuggested?: boolean;
}

export interface OcrAccountOrderService {
  id?: string;
  name?: string;
}

export interface OcrAccountOrderOption {
  index: string;
  accountId?: string;
  order?: number;
  accountType?: IOcrAccountOrderDtoAccountTypeEnum;
  planName?: string;
  availableBalance?: number;
  proofOfExpensesType?: string;
  coverageDateStart?: string;
  coverageDateEnd?: string;
  submitClaimsDateEnd?: string;
  services?: OcrAccountOrderService[];
}

export interface OcrResult {
  selectedProvider?: OcrProvider;
  selectedServiceFor?: OcrServiceFor;
  selectedService?: OcrService;
  selectedAmount?: OcrAmount;
  selectedServiceDate?: OcrServiceDate;
  selectedAccountNumber?: string;
  selectedInvoiceNumber?: string;
  providers?: OcrProviderOption[];
  addressOptions?: Address[];
  serviceForOptions?: OcrServiceFor[];
  serviceOptions?: OcrService[];
  amountOptions?: OcrAmount[];
  serviceDateOptions?: OcrServiceDate[];
  accountOrders?: OcrAccountOrderOption[];
  availableAccounts?: OcrAccountOrderOption[];
  accountOrdersArraysWithUniqServices?: OcrAccountOrderOption[][];
  isReceiptRequired?: boolean;
  warnings?: OcrWarning[];
}
